import { useState } from 'react';
import { CheckCircle2, GitCompare, Code2, ChevronLeft, Key, FileJson, Lock, Clock, Link, Hash, Shield, Users, FileCode, Settings, Sun, Moon } from 'lucide-react';
import SchemaValidator from './SchemaValidator';
import DiffViewer from './DiffViewer';
import CodeSnippetGenerator from './CodeSnippetGenerator';
import JWTDebugger from './JWTDebugger';
import JSONFormatter from './JSONFormatter';
import Base64Tool from './Base64Tool';
import TimestampConverter from './TimestampConverter';
import URLTool from './URLTool';
import UUIDGenerator from './UUIDGenerator';
import HashGenerator from './HashGenerator';
import MockDataGenerator from './MockDataGenerator';
import YAMLConverter from './YAMLConverter';
import ENVConverter from './ENVConverter';

type ToolView = 'diff' | 'validator' | 'snippets' | 'jwt' | 'json' | 'base64' | 'timestamp' | 'url' | 'uuid' | 'hash' | 'mock' | 'yaml' | 'env';

interface DeveloperToolsPanelProps {
  onClose?: () => void;
  initialTool?: ToolView;
}

export default function DeveloperToolsPanel({ onClose, initialTool = 'diff' }: DeveloperToolsPanelProps) {
  const [activeView, setActiveView] = useState<ToolView>(initialTool);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const tools = [
    {
      id: 'diff' as ToolView,
      label: 'Diff Viewer',
      icon: GitCompare,
      description: 'Compare files'
    },
    {
      id: 'jwt' as ToolView,
      label: 'JWT Debugger',
      icon: Key,
      description: 'Decode JWT tokens'
    },
    {
      id: 'json' as ToolView,
      label: 'JSON Formatter',
      icon: FileJson,
      description: 'Format/minify JSON'
    },
    {
      id: 'base64' as ToolView,
      label: 'Base64 Tool',
      icon: Lock,
      description: 'Encode/decode Base64'
    },
    {
      id: 'timestamp' as ToolView,
      label: 'Timestamp',
      icon: Clock,
      description: 'Convert timestamps'
    },
    {
      id: 'url' as ToolView,
      label: 'URL Tool',
      icon: Link,
      description: 'Encode/decode URLs'
    },
    {
      id: 'uuid' as ToolView,
      label: 'UUID Generator',
      icon: Hash,
      description: 'Generate UUIDs'
    },
    {
      id: 'hash' as ToolView,
      label: 'Hash Generator',
      icon: Shield,
      description: 'Generate hashes'
    },
    {
      id: 'mock' as ToolView,
      label: 'Mock Data',
      icon: Users,
      description: 'Generate mock data'
    },
    {
      id: 'yaml' as ToolView,
      label: 'YAML Converter',
      icon: FileCode,
      description: 'YAML ↔ JSON'
    },
    {
      id: 'env' as ToolView,
      label: 'ENV to YAML',
      icon: Settings,
      description: 'Convert ENV files'
    },
    {
      id: 'validator' as ToolView,
      label: 'Schema Validator',
      icon: CheckCircle2,
      description: 'Validate JSON'
    },
    {
      id: 'snippets' as ToolView,
      label: 'Code Generator',
      icon: Code2,
      description: 'Generate API code'
    },
  ];

  return (
    <div className={`fixed inset-0 z-50 bg-white flex overflow-hidden ${darkMode ? 'dark' : ''}`}>
      {/* Left Sidebar */}
      <div
        className={`${
          sidebarCollapsed ? 'w-20' : 'w-64'
        } bg-gradient-to-b from-gray-50 to-gray-100 dark:from-dark-surface dark:to-dark-bg border-r border-gray-200 dark:border-dark-border flex flex-col transition-all duration-300`}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center justify-between">
            {!sidebarCollapsed && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-coral-500 to-teal-500 rounded-lg flex items-center justify-center">
                  <Code2 className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text">Dev Tools</h3>
                  <p className="text-xs text-gray-500 dark:text-dark-muted">Playground</p>
                </div>
              </div>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1.5 hover:bg-gray-200 dark:hover:bg-dark-border rounded-lg transition-colors"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <ChevronLeft
                className={`w-4 h-4 text-gray-600 dark:text-dark-muted transition-transform ${
                  sidebarCollapsed ? 'rotate-180' : ''
                }`}
              />
            </button>
          </div>
        </div>

        {/* Tools Navigation */}
        <div className="flex-1 p-3 space-y-2 overflow-y-auto">
          {tools.map((tool) => {
            const Icon = tool.icon;
            const isActive = activeView === tool.id;

            return (
              <button
                key={tool.id}
                onClick={() => setActiveView(tool.id)}
                className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-3' : 'gap-3 px-3'} py-3 rounded-xl transition-all ${
                  isActive
                    ? 'bg-gradient-to-r from-coral-500 to-teal-500 text-white shadow-lg shadow-coral-500/30'
                    : 'bg-white dark:bg-dark-surface text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-border border border-gray-200 dark:border-dark-border'
                }`}
                title={sidebarCollapsed ? tool.label : undefined}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-600 dark:text-dark-muted'}`} />
                {!sidebarCollapsed && (
                  <div className="text-left flex-1 min-w-0">
                    <div className={`text-sm font-medium ${isActive ? 'text-white' : 'text-gray-900 dark:text-dark-text'}`}>
                      {tool.label}
                    </div>
                    <div className={`text-xs ${isActive ? 'text-white/80' : 'text-gray-500 dark:text-dark-muted'}`}>
                      {tool.description}
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer - Close button */}
        {onClose && (
          <div className="p-3 border-t border-gray-200 dark:border-dark-border">
            <button
              onClick={onClose}
              className="w-full px-3 py-2 bg-white dark:bg-dark-surface text-gray-700 dark:text-dark-text border border-gray-200 dark:border-dark-border rounded-lg hover:bg-gray-100 dark:hover:bg-dark-border transition-colors text-sm font-medium"
            >
              {sidebarCollapsed ? '×' : 'Close Dev Tools'}
            </button>
          </div>
        )}
      </div>

      {/* Right Workspace */}
      <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-dark-bg">
        {/* Workspace Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-surface">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text">
                {tools.find(t => t.id === activeView)?.label}
              </h2>
              <p className="text-sm text-gray-500 dark:text-dark-muted">
                {tools.find(t => t.id === activeView)?.description}
              </p>
            </div>

            {/* Theme Toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 hover:bg-gray-200 dark:hover:bg-dark-border rounded-lg transition-colors"
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? (
                <Sun className="w-5 h-5 text-gray-600 dark:text-dark-muted" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600 dark:text-dark-muted" />
              )}
            </button>
          </div>
        </div>

        {/* Workspace Content - Full height, scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeView === 'diff' && <DiffViewer onAutoCollapse={() => setSidebarCollapsed(true)} />}
          {activeView === 'jwt' && <JWTDebugger />}
          {activeView === 'json' && <JSONFormatter />}
          {activeView === 'base64' && <Base64Tool />}
          {activeView === 'timestamp' && <TimestampConverter />}
          {activeView === 'url' && <URLTool />}
          {activeView === 'uuid' && <UUIDGenerator />}
          {activeView === 'hash' && <HashGenerator />}
          {activeView === 'mock' && <MockDataGenerator />}
          {activeView === 'yaml' && <YAMLConverter />}
          {activeView === 'env' && <ENVConverter />}
          {activeView === 'validator' && <SchemaValidator />}
          {activeView === 'snippets' && <CodeSnippetGenerator />}
        </div>
      </div>
    </div>
  );
}
